/**
 * RestoID — Yandex ID Suggest widget controller.
 * ---------------------------------------------------------------------------
 * Implements the official Yandex ID Suggest "token page" protocol. Acquisition
 * happens in-page (SDK + handshake + widget mount); the actual Logto sign-in is
 * delegated to the shared `useSocialCompletion` hook, which runs the PRISTINE
 * upstream verify/identify path (the same one SocialSignInWebCallback uses).
 *
 * Flow:
 *   1. one handshake: ask Logto for the social authorization URL with our token
 *      page as redirect_uri — yields the verificationId and the connector's
 *      client_id (always current, no redeploy);
 *   2. mount the Suggest widget (YaAuthSuggest.init) with that client_id +
 *      redirect_uri (public/yandex-token.html) + state + a language-matched icon;
 *   3. handler() resolves IN-PAGE with the OAuth result (posted back by the
 *      token page via YaSendSuggestToken);
 *   4. widget-specific gates (OAuth-error / best-effort state / terms) then
 *      `useSocialCompletion` → verify → identify → redirect (incl. register/link).
 *
 * The standard (placeholder) button is NOT handled here — it uses the upstream
 * `useSocial().invokeSocialSignIn` redirect flow, which already gates terms.
 */
import {
  AgreeToTermsPolicy,
  VerificationType,
  type ExperienceSocialConnector,
} from '@logto/schemas';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { getSocialAuthorizationUrl } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useTerms from '@/hooks/use-terms';
import { generateState } from '@/utils/social-connectors';

import { useLang } from '../copy';

import { readRidTheme } from './use-rid-theme';
import useSocialCompletion from './use-social-completion';

export const YANDEX_WIDGET_PARENT_ID = 'restoid-yandex-widget';
const YANDEX_TOKEN_PAGE_PATH = '/yandex-token.html';

const MAIN_SDK_SRC =
  'https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js';
const SDK_TIMEOUT_MS = 10_000;
// Handler() resolves only when the token page posts the result back. If that
// frame is blocked (3rd-party frame/cookie blocking, ad-blockers, redirect_uri
// not yet whitelisted) it can hang forever — bound it so we can recover.
const HANDLER_TIMEOUT_MS = 300_000;
// The Suggest button renders its label/avatar a beat after init() resolves (it
// first shows just the logo); wait up to this long for the full button before
// crossfading, else reveal it anyway (it is already functional).
const WIDGET_RENDER_TIMEOUT_MS = 5000;

// Static Suggest button options; `buttonIcon` (ya = Cyrillic Я, yaEng = Latin Y)
// and `buttonTheme` are added at init time, matching the placeholder.
//
// NOTE on `buttonTheme`: it names the BACKGROUND the button sits on, not the
// button's own color. Yandex's `main` view is a BLACK button with white text, and
// it inverts to white when buttonTheme is 'dark'. So:
//     page light  -> buttonTheme 'light' -> black button   (the spec's main version)
//     page dark   -> buttonTheme 'dark'  -> white button
// Getting this backwards renders a near-black button on our near-black page.
const YANDEX_OPTIONS = {
  view: 'button',
  parentId: YANDEX_WIDGET_PARENT_ID,
  buttonView: 'main',
  buttonSize: 'xxl',
  buttonBorderRadius: '12',
} as const;

type YaInitResult = { handler: () => Promise<Record<string, unknown>> };
declare global {
  // `interface` (not `type`) is required to merge with the global Window.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    YaAuthSuggest?: {
      init: (
        oauthParams: {
          client_id: string;
          response_type: string;
          redirect_uri: string;
          state?: string;
        },
        tokenPageOrigin: string,
        suggestOptions?: Record<string, unknown>
      ) => Promise<YaInitResult>;
    };
  }
}

type Handshake = {
  state: string;
  redirectUri: string;
  verificationId: string;
};

/** Load the Suggest SDK once, with a timeout; a failed load is not cached forever. */
let sdkPromise: Promise<void> | undefined;
const loadYandexSdk = (): Promise<void> => {
  if (window.YaAuthSuggest) {
    return Promise.resolve();
  }
  sdkPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = MAIN_SDK_SRC;
    script.async = true;
    const timer = setTimeout(() => {
      script.remove();
      reject(new Error('Yandex SDK load timed out'));
    }, SDK_TIMEOUT_MS);
    script.addEventListener('load', () => {
      clearTimeout(timer);
      resolve();
    });
    script.addEventListener('error', () => {
      clearTimeout(timer);
      script.remove();
      reject(new Error('Yandex SDK failed to load'));
    });
    document.head.append(script);
    // Reset the cached promise on failure so a later call can retry; clearest as a
    // .catch on the cached promise here.
    // eslint-disable-next-line promise/prefer-await-to-then
  }).catch((error: unknown) => {
    sdkPromise = undefined; // Allow a later retry
    throw error;
  });
  return sdkPromise;
};

const log = (message: string, error?: unknown) => {
  // Keep widget failures observable in production (degrade to the standard button).

  console.error(`[YandexSignIn] ${message}`, error ?? '');
};

/**
 * Resolve once the Suggest button has rendered BOTH its label and its avatar — so
 * the crossfade never reveals the half-built widget (logo only) — or after a
 * timeout (the button is functional regardless, so we reveal it either way).
 */
const waitForWidgetButton = (timeoutMs: number): Promise<void> =>
  new Promise((resolve) => {
    const parent = document.querySelector(`#${YANDEX_WIDGET_PARENT_ID}`);
    if (!parent) {
      resolve();
      return;
    }
    const rendered = (): boolean => {
      const label = parent.querySelector('.yaPersonalButtonText')?.textContent.trim();
      const avatar = parent.querySelector('.yaPersonalButtonAvatar');
      const hasAvatar = avatar ? getComputedStyle(avatar).backgroundImage !== 'none' : false;
      return Boolean(label) && hasAvatar;
    };
    if (rendered()) {
      resolve();
      return;
    }
    const observer = new MutationObserver(() => {
      if (rendered()) {
        clearTimeout(timer);
        observer.disconnect();
        resolve();
      }
    });
    const timer = setTimeout(() => {
      observer.disconnect();
      resolve();
    }, timeoutMs);
    observer.observe(parent, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style'],
    });
  });

/**
 * Drives the Yandex ID Suggest widget. Returns whether the widget has mounted
 * (so the caller can crossfade it over the standard placeholder button).
 */
const useYandexWidget = (connector: ExperienceSocialConnector): { widgetReady: boolean } => {
  const { t } = useTranslation();
  const lang = useLang();
  const { agreeToTermsPolicy, termsValidation } = useTerms();
  const { setVerificationId } = useContext(UserInteractionContext);
  const asyncGetAuthorizationUrl = useApi(getSocialAuthorizationUrl);
  const { complete: completeSocial, backToSignIn } = useSocialCompletion(connector.id);

  const [widgetReady, setWidgetReady] = useState(false);
  // Tracks mount so the post-handler completion chain never mutates context /
  // navigates after the component is gone (the setup effect uses its own
  // `cancelled` flag; completion runs past that scope).
  const mountedRef = useRef(true);

  /** Widget-specific gates on the OAuth result, then delegate to shared completion. */
  const complete = useCallback(
    async (handshake: Handshake, data: Record<string, unknown>) => {
      // Surface an explicit OAuth error (e.g. access_denied) instead of POSTing a
      // codeless payload that would only yield a generic server error.
      if (typeof data.error === 'string') {
        backToSignIn(
          typeof data.error_description === 'string'
            ? data.error_description
            : t('error.invalid_connector_auth')
        );
        return;
      }

      // Best-effort CSRF: the in-page Suggest result may not echo `state` back, so
      // we only reject an explicit mismatch. The real binding is the server-bound
      // verificationId plus the same-origin postMessage from yandex-token.html.
      const returnedState = typeof data.state === 'string' ? data.state : undefined;
      if (returnedState && returnedState !== handshake.state) {
        backToSignIn(t('error.invalid_connector_auth'));
        return;
      }

      // Terms gate (Manual policy). The widget authorizes in-place, so we gate
      // before completing; on decline, reveal the standard button (whose own
      // terms-gated redirect lets the user retry) — the one-shot widget is spent.
      if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
        if (mountedRef.current) {
          setWidgetReady(false);
        }
        return;
      }

      await completeSocial({
        verificationId: handshake.verificationId,
        connectorData: { redirectUri: handshake.redirectUri, ...data },
        isActive: () => mountedRef.current,
      });
    },
    [agreeToTermsPolicy, backToSignIn, completeSocial, t, termsValidation]
  );

  // Keep the latest `complete` reachable from the run-once setup effect.
  const completeRef = useRef(complete);

  completeRef.current = complete;

  useEffect(() => {
    // Empty deps => runs once per real mount; the `cancelled` flag (not a
    // persistent ref) aborts the throwaway StrictMode pass without permanently
    // disabling the widget.
    let cancelled = false;

    mountedRef.current = true;
    // Read once at mount: the language switch does a full page reload (chrome
    // setLang -> location.assign), so the component always remounts with the
    // freshly-resolved i18next language — the read-once icon can't go stale.
    const buttonIcon = lang === 'en' ? 'yaEng' : 'ya';
    // Read once at mount, like the icon: YandexSignIn is keyed on the theme in
    // SignIn, so a theme switch remounts this hook rather than leaving a widget
    // rendered in the previous variant (the Suggest SDK has no re-theme call).
    const buttonTheme = readRidTheme() === 'light' ? 'light' : 'dark';

    void (async () => {
      /* eslint-disable @typescript-eslint/no-unnecessary-condition -- `cancelled` is
         flipped by the cleanup below, after this closure is created; TS's flow
         analysis can't see that and wrongly flags these abort guards. */
      try {
        await loadYandexSdk();
        if (cancelled || !window.YaAuthSuggest) {
          return;
        }

        // The Suggest widget renders its account preview on mount, so the handshake
        // is intentionally eager (one initInteraction + authorization-uri per view).
        // Logto issues the verificationId and embeds the connector's client_id in
        // the authorization URL we parse out.
        const state = generateState();
        const redirectUri = `${window.location.origin}${YANDEX_TOKEN_PAGE_PATH}`;
        const [error, result] = await asyncGetAuthorizationUrl(connector.id, state, redirectUri);
        if (cancelled) {
          return;
        }
        if (error || !result) {
          log('authorization-uri request failed', error);
          return;
        }
        setVerificationId(VerificationType.Social, result.verificationId);

        let clientId = '';
        try {
          clientId = new URL(result.authorizationUri).searchParams.get('client_id') ?? '';
        } catch (parseError) {
          log('could not parse client_id from authorization uri', parseError);
        }
        if (!clientId) {
          log('connector misconfigured: no client_id');
          return;
        }

        const handshake: Handshake = { state, redirectUri, verificationId: result.verificationId };

        const { handler } = await window.YaAuthSuggest.init(
          {
            client_id: clientId,
            response_type: 'code',
            redirect_uri: redirectUri,
            state,
          },
          window.location.origin,
          { ...YANDEX_OPTIONS, buttonIcon, buttonTheme }
        );
        if (cancelled) {
          return;
        }

        // Don't crossfade onto a half-built widget: wait until it has rendered its
        // label + avatar (it briefly shows the logo alone right after init).
        await waitForWidgetButton(WIDGET_RENDER_TIMEOUT_MS);
        if (cancelled) {
          return;
        }
        setWidgetReady(true);

        const data = await Promise.race([
          handler(),
          new Promise<never>((_resolve, reject) => {
            setTimeout(() => reject(new Error('Yandex handler timed out')), HANDLER_TIMEOUT_MS);
          }),
        ]);
        if (cancelled) {
          return;
        }
        await completeRef.current(handshake, data);
      } catch (error) {
        // Skip when cancelled: an unmounted instance isn't "unavailable", and its
        // late handler-timeout shouldn't log or touch state.
        if (!cancelled) {
          log('widget unavailable (falling back to the standard button)', error);
          setWidgetReady(false); // Reveal the working standard button
        }
      }
      /* eslint-enable @typescript-eslint/no-unnecessary-condition */
    })();

    return () => {
      cancelled = true;

      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  return { widgetReady };
};

export default useYandexWidget;
